import { callDelete, get, post, put } from "@/services/api.services";
import { SavedObject } from "@/services/commonModels";
import { RegistryEntry } from "@/services/registries/registriesAPI";

export type TreatmentComponentType = "active" | "inactive";

export type BaseTreatmentComponent = {
    registryEntryId: string;
    type: TreatmentComponentType;
    concentration: string | null;
    unitsRegistryEntryId: string | null;
    treatmentId: string;
};

export type TreatmentComponentSetupConfig = Omit<BaseTreatmentComponent, "type" | "treatmentId">;

export type TreatmentSetupConfig = Omit<BaseTreatment, "experimentId"> & {
    actives: TreatmentComponentSetupConfig[];
    inactives: TreatmentComponentSetupConfig[];
};

export type SavedBaseTreatmentComponent = BaseTreatmentComponent & SavedObject<string>;

export type TreatmentComponent = SavedBaseTreatmentComponent & {
    units: RegistryEntry | null;
};

export type TreatmentComponentFormConfig = {
    treatmentComponentOptions: RegistryEntry[];
    unitsOptions: RegistryEntry[];
};

export type TreatmentFormConfig = TreatmentComponentFormConfig & {
    treatmentTypeOptions: RegistryEntry[];
};

export type BaseTreatment = {
    name: string;
    number: number;
    typeRegistryEntryId: string;
    experimentId: number;
};

export type SavedBaseTreatment = BaseTreatment & SavedObject<string>;

export type Treatment = SavedBaseTreatment & {
    name: string;
    number: number;
    type: RegistryEntry;
    actives: TreatmentComponent[];
    inactives: TreatmentComponent[];
};

export const treatmentsAPI = {
    fetchTreatmentsSetupFile: async (experimentCode?: string) => {
        return get(`/treatments/setup/${experimentCode}`, {
            responseType: "blob",
        });
    },
    fetchTreatmentsForExperimentCode: async (experimentCode: string): Promise<Treatment[]> => {
        return get(`/treatments/${experimentCode}`);
    },
    fetchTreatmentComponentFormConfig: async (): Promise<TreatmentComponentFormConfig> => {
        return get(`/treatments/treatmentComponents/formConfig`);
    },
    fetchTreatmentFormConfig: async (): Promise<TreatmentFormConfig> => {
        return get(`/treatments/formConfig`);
    },
    updateTreatmentComponent: async (
        treatmentComponent: SavedBaseTreatmentComponent
    ): Promise<void> => {
        const id = treatmentComponent.id;
        return put(`/treatments/treatmentComponents/${id}`, treatmentComponent);
    },
    createTreatmentsFromSetupFile: async (
        experimentCode: string,
        formData: FormData
    ): Promise<Treatment[]> => {
        return post(`/treatments/${experimentCode}/importSetupFile`, formData);
    },
    createTreatment: async (
        experimentCode: string,
        treatmentSetupConfig: TreatmentSetupConfig
    ): Promise<Treatment> => {
        return post(`/treatments/${experimentCode}`, treatmentSetupConfig);
    },
    updateTreatment: async (
        experimentCode: string,
        id: string,
        treatmentSetupConfig: TreatmentSetupConfig,
        treatmentComponentIdsToDelete: string[]
    ): Promise<Treatment> => {
        const body = {
            treatmentSetupConfig,
            treatmentComponentIdsToDelete,
        };
        return put(`/treatments/${experimentCode}/${id}`, body);
    },
    deleteTreatment: async (experimentCode: string, id: string): Promise<void> => {
        return callDelete(`/treatments/${experimentCode}/${id}`);
    },
    deleteAllTreatmentsForExperimentCode: async (experimentCode: string): Promise<void> => {
        return callDelete(`treatments/${experimentCode}`);
    },
};
